import { ClickAwayListener, ListItem, Stack, TextField, Typography } from "@mui/material";
import type { JSX } from "react";
import { useState } from "react";
import AutocompleteCreatable from "@entrilia/shared/components/AutocompleteCreatable";
import type { Maybe } from "@entrilia/shared/types";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import { invalidResult, validResult } from "@entrilia/shared/utilities/validators";
import type { FieldAttribute, MultiLabelSelectOption } from "../../../api/types/objectTypes";
import { useFieldValuesContext } from "../FieldValuesContext";
import { findNextEntityInput, findPreviousEntityInput } from "../entityFieldNavigator";
import FieldValueWrapper from "./FieldValueWrapper";

interface Props {
  fieldId: string;
  value?: Maybe<string>;
  options: MultiLabelSelectOption[];
  fieldAttributes: FieldAttribute[];
  onChange: (value: string, validationResult: ValidationResult) => void;
  fieldInputId?: string;
  displayValueComponent?: JSX.Element | JSX.Element[] | null;
  onCreateOption?: () => void;
}

interface SelectState {
  isOpened: boolean;
  isEdit: boolean;
  validationResult: ValidationResult;
  selectedValue: Maybe<string>;
}

const EntityFieldSelectComponent = ({
  fieldId,
  value,
  options,
  fieldAttributes,
  onChange,
  fieldInputId,
  displayValueComponent,
  onCreateOption,
}: Props) => {
  const { validateUniqueFieldValue } = useFieldValuesContext();

  const [state, setState] = useState<SelectState>(() => ({
    selectedValue: value,
    isOpened: false,
    isEdit: false,
    validationResult: validResult(),
  }));

  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    setState((current) => ({ ...current, selectedValue: value }));
  }

  const validate = async () => {
    const { selectedValue } = state;
    if (fieldAttributes.includes("Required") && !selectedValue) {
      return invalidResult("Required field. Please select the necessary value in the select field.");
    }

    const isValid = options.some((o) => o.value === selectedValue) || !selectedValue || selectedValue === value;
    if (!isValid) {
      return invalidResult("Invalid value");
    }

    if (fieldAttributes.includes("ObjectIdentifier")) {
      const uniqueValidationResult = await validateUniqueFieldValue(fieldId, selectedValue);
      if (!uniqueValidationResult.isValid) {
        return uniqueValidationResult;
      }
    }

    return validResult();
  };

  const handleValueOnLeave = async () => {
    const validationResult = await validate();

    setState((current) => ({
      ...current,
      isOpened: !validationResult.isValid,
      isEdit: !validationResult.isValid,
      validationResult,
    }));

    onChange(state.selectedValue || "", validationResult);
  };

  const handleEditChange = (edit: boolean) => {
    if (edit) {
      setState((current) => ({ ...current, isOpened: true, isEdit: true, validationResult: validResult() }));
    } else {
      setState((current) => ({ ...current, isOpened: false, isEdit: false }));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Tab" && fieldInputId) {
      const nextElement = e.shiftKey ? findPreviousEntityInput(fieldInputId) : findNextEntityInput(fieldInputId);
      if (nextElement) {
        e.preventDefault();
        nextElement.click();
      }
      return;
    }
  };

  const selectedOption = state.selectedValue ? options.find((o) => o.value === state.selectedValue) : undefined;

  return (
    <>
      {!state.isEdit && (
        <FieldValueWrapper
          id={fieldInputId}
          isReadonly={fieldAttributes.includes("ReadOnly")}
          displayValue={selectedOption?.label || state.selectedValue || ""}
          isEdit={false}
          onEditChange={handleEditChange}
          emptyPlaceholder="Select"
          displayValueComponent={displayValueComponent}
        />
      )}
      {state.isEdit && (
        <ClickAwayListener onClickAway={handleValueOnLeave}>
          <AutocompleteCreatable
            fullWidth
            open={state.isOpened}
            options={options}
            onClose={() => setState((current) => ({ ...current, isOpened: false }))}
            getOptionLabel={({ label }) => label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            value={selectedOption ?? null}
            onChange={(_e, newValue) => {
              setState((current) => ({
                ...current,
                validationResult: validResult(),
                selectedValue: typeof newValue === "string" ? newValue : newValue?.value,
              }));
            }}
            onCreateOption={onCreateOption}
            renderInput={(params) => (
              <TextField
                autoFocus
                {...params}
                error={!state.validationResult.isValid}
                helperText={state.validationResult.error}
                onKeyDown={handleKeyDown}
              />
            )}
            renderOption={({ key: _key, ...props }, option) => (
              <ListItem key={option.value} {...props}>
                <Stack>
                  <Typography>{option.label}&nbsp;</Typography>
                  {option.secondaryLabel && (
                    <Typography color="textSecondary">{option.secondaryLabel}&nbsp;</Typography>
                  )}
                </Stack>
              </ListItem>
            )}
          />
        </ClickAwayListener>
      )}
    </>
  );
};

export default EntityFieldSelectComponent;
